/* eslint-disable */
/**
 * offlinePlaylist.service.js
 * Service for managing explicit offline downloads of playlists and songs,
 * storing data across CacheStorage (Service Worker), IndexedDB (dbService), and cacheInstance.
 */

import dbService from '@/service/db.service';
import playlistService from '@/service/playlist.service';
import { appEvents } from '@/store/app.events';

const REGISTRY_KEY = 'triad_offline_playlists_registry';
const CONTENT_CACHE_NAME = 'triad-content-cache';

/**
 * Consistently extracts song ID from song items across all formats
 */
const extractSongId = (s) => {
    if (!s) return '';
    return String(s.song_id || s.songID || s.song || s.data?.id || s.data?._id || s.id || s._id || '');
};

class OfflinePlaylistService {
    constructor() {
        this.downloadingPlaylists = new Set();
        this.registryCache = null;
        if (typeof window !== 'undefined') {
            this.loadRegistry();
        }
    }

    async loadRegistry() {
        try {
            const data = await dbService.getItem(REGISTRY_KEY);
            this.registryCache = (data && typeof data === 'object') ? data : {};
            return this.registryCache;
        } catch (e) {
            console.warn('[OfflinePlaylistService] Error loading offline registry:', e);
            this.registryCache = {};
            return this.registryCache;
        }
    }

    async getRegistry() {
        if (this.registryCache !== null) {
            return this.registryCache;
        }
        return await this.loadRegistry();
    }

    async saveRegistry(registry) {
        this.registryCache = registry;
        try {
            await dbService.setItem(REGISTRY_KEY, registry);
            appEvents.emit('offline:registry-updated', registry);
        } catch (e) {
            console.warn('[OfflinePlaylistService] Error saving offline registry:', e);
        }
    }

    /**
     * Check if a playlist is downloaded and available offline
     */
    async isPlaylistDownloaded(playlistId) {
        if (!playlistId) return false;
        const registry = await this.getRegistry();
        return Boolean(registry[playlistId] && registry[playlistId].status === 'downloaded');
    }

    /**
     * Check if a playlist is currently downloading
     */
    isPlaylistDownloading(playlistId) {
        return this.downloadingPlaylists.has(playlistId);
    }

    /**
     * Get all downloaded playlists from registry
     */
    async getDownloadedPlaylists() {
        const registry = await this.getRegistry();
        return Object.values(registry).filter(item => item.status === 'downloaded');
    }

    /**
     * Check if current user is allowed to download a playlist offline
     * - Paid tiers (musician_tier, backstage_tier, admin_tier, custom_tier): unlimited
     * - Free tier: level 1 limit (maximum 1 offline playlist at a time)
     * @param {string} playlistId - ID of playlist being checked/downloaded
     * @param {Object} userInfo - User info object (optional, defaults to localStorage or window.store)
     * @returns {Promise<{ allowed: boolean, reason?: string, currentCount: number, maxAllowed: number }>}
     */
    async checkDownloadEligibility(playlistId, userInfo = null) {
        let user = userInfo;
        if (!user && typeof window !== 'undefined') {
            try {
                const stored = localStorage.getItem('triad_offline_user');
                if (stored) user = JSON.parse(stored);
            } catch (_) {}
        }

        const tier = user?.membershipTier || user?.membership_tier;
        const isFree = (!tier || tier === 'free_membership' || tier === 'free' || tier === 'free_tier') ||
                       (tier !== 'musician_tier' && tier !== 'admin_tier' && tier !== 'backstage_tier' && tier !== 'custom_tier');

        // Paid tiers have unlimited offline downloads
        if (!isFree) {
            return { allowed: true, currentCount: 0, maxAllowed: Infinity };
        }

        // Free tier: allowed up to 1 playlist offline (Level 1)
        const downloaded = await this.getDownloadedPlaylists();
        const alreadyDownloaded = downloaded.some(item => (item.id || item._id) === playlistId);

        if (alreadyDownloaded) {
            return { allowed: true, currentCount: downloaded.length, maxAllowed: 1 };
        }

        if (downloaded.length >= 1) {
            return {
                allowed: false,
                reason: 'FREE_LIMIT_REACHED',
                currentCount: downloaded.length,
                maxAllowed: 1
            };
        }

        return { allowed: true, currentCount: downloaded.length, maxAllowed: 1 };
    }

    /**
     * Check if a specific song is downloaded offline
     */
    async isSongDownloaded(playlistId, songId) {
        if (!playlistId || !songId) return false;
        // Check window.cacheInstance first
        if (typeof window !== 'undefined' && window.cacheInstance) {
            const key = playlistService.PLAYLIST_SONG_CACHE_KEY
                .replace('[id]', playlistId)
                .replace('[songId]', songId);
            const inMem = window.cacheInstance.getItem(key);
            if (inMem) return true;
        }
        // Check IndexedDB
        const dbKey = `playlist::${playlistId}::song::${songId}`;
        const inDb = await dbService.getItem(dbKey);
        if (inDb) return true;

        // Check if playlist is fully downloaded
        return await this.isPlaylistDownloaded(playlistId);
    }

    /**
     * Downloads a playlist and all its songs in batch
     * @param {Object} playlist - Playlist object with id/_id, name/title, and songs array
     * @param {Function} onProgress - Progress callback ({ current, total, percentage, songTitle })
     */
    async downloadPlaylist(playlist, onProgress = null) {
        if (!playlist) throw new Error('Playlist is required');
        const playlistId = playlist.id || playlist._id;
        if (!playlistId) throw new Error('Playlist ID is required');

        // Check tier limits (free member allowed only level 1 = 1 offline playlist)
        const eligibility = await this.checkDownloadEligibility(playlistId);
        if (!eligibility.allowed) {
            const err = new Error('FREE_LIMIT_REACHED');
            err.code = 'FREE_LIMIT_REACHED';
            throw err;
        }

        if (this.downloadingPlaylists.has(playlistId)) {
            console.warn(`[OfflinePlaylistService] Playlist ${playlistId} is already downloading.`);
            return;
        }

        this.downloadingPlaylists.add(playlistId);
        appEvents.emit('offline:playlist-download-started', { playlistId });

        try {
            // 1. Fetch fresh playlist data
            let freshPlaylist = null;
            try {
                freshPlaylist = await playlistService.fetchPlaylist({ id: playlistId, force: true });
            } catch (err) {
                console.warn('[OfflinePlaylistService] fetchPlaylist failed, using provided data:', err);
                freshPlaylist = playlist;
            }

            const pData = freshPlaylist || playlist;
            const songs = Array.isArray(pData.songs) ? pData.songs : [];
            const total = songs.length;

            // Cache playlist object
            const playlistCacheKey = playlistService.PLAYLIST_CACHE_KEY.replace('[id]', playlistId);
            if (typeof window !== 'undefined' && window.cacheInstance) {
                window.cacheInstance.setItem(playlistCacheKey, pData, playlistService.ttl);
            }
            await dbService.setItem(playlistCacheKey, pData);

            if (onProgress) {
                onProgress({ current: 0, total, percentage: 0, songTitle: '' });
            }

            // 2. Fetch and cache each song in chunks (concurrency: 2)
            const concurrency = 2;
            let completed = 0;

            for (let i = 0; i < songs.length; i += concurrency) {
                const chunk = songs.slice(i, i + concurrency);
                await Promise.all(chunk.map(async (songItem) => {
                    const sId = songItem.data?.id || songItem.data?._id || songItem.songID || songItem.song || songItem.id || songItem._id;
                    const rawSongId = songItem.song_id || songItem.songID || songItem.song;
                    const songTitle = songItem.data?.title || songItem.title || `Música ${completed + 1}`;

                    if (sId) {
                        try {
                            // Fetch full song details with processed chords/lyrics
                            const songData = await playlistService.fetchSong({
                                id: playlistId,
                                songId: sId,
                                process: true,
                                force: true
                            });

                            if (songData) {
                                const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY
                                    .replace('[id]', playlistId)
                                    .replace('[songId]', sId);
                                
                                // Store full song data in IndexedDB (dbService), avoiding localStorage quota limits
                                await dbService.setItem(songKey, songData);

                                // If raw song ID is also present, prefetch and cache in IndexedDB
                                if (rawSongId && rawSongId !== sId) {
                                    const rawSongKey = `song::${rawSongId}`;
                                    await dbService.setItem(rawSongKey, songData);
                                }
                            }
                        } catch (songErr) {
                            console.warn(`[OfflinePlaylistService] Failed to cache song ${sId}:`, songErr);
                        }
                    }

                    completed++;
                    const percentage = total > 0 ? Math.round((completed / total) * 100) : 100;
                    if (onProgress) {
                        onProgress({ current: completed, total, percentage, songTitle });
                    }
                    appEvents.emit('offline:playlist-download-progress', {
                        playlistId,
                        current: completed,
                        total,
                        percentage,
                        songTitle
                    });
                }));
            }

            // 3. Mark in registry
            const registry = await this.getRegistry();
            const songIds = songs.map(s => extractSongId(s)).filter(Boolean);
            const rawServerUpdatedAt = pData.updated_at || pData.updatedAt || pData.created_at;
            let serverUpdatedAt = '';
            if (rawServerUpdatedAt) {
                const parsedDate = new Date(rawServerUpdatedAt);
                serverUpdatedAt = !isNaN(parsedDate.getTime()) ? parsedDate.toISOString() : String(rawServerUpdatedAt);
            }
            const songsChecksum = this.generateSongsChecksum(songs, pData);

            registry[playlistId] = {
                id: playlistId,
                name: pData.name || pData.title || 'Playlist',
                description: pData.description || '',
                cover: pData.cover || null,
                tags: Array.isArray(pData.tags) ? pData.tags : [],
                songCount: total,
                songIds,
                serverUpdatedAt,
                songsChecksum,
                downloadedAt: Date.now(),
                updatedAt: Date.now(),
                status: 'downloaded'
            };
            await this.saveRegistry(registry);

            appEvents.emit('offline:playlist-downloaded', { playlistId, count: total });
            return true;
        } finally {
            this.downloadingPlaylists.delete(playlistId);
        }
    }

    /**
     * Removes a playlist and its songs from offline storage
     */
    async removePlaylist(playlistId) {
        if (!playlistId) return false;

        try {
            // 1. Get playlist details to find songs
            const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace('[id]', playlistId);
            const cachedPlaylist = (window.cacheInstance && window.cacheInstance.getItem(playlistKey)) 
                || await dbService.getItem(playlistKey);

            // 2. Remove song cache keys
            if (cachedPlaylist && Array.isArray(cachedPlaylist.songs)) {
                for (const song of cachedPlaylist.songs) {
                    const sId = song.data?.id || song.data?._id || song.songID || song.song || song.id || song._id;
                    if (sId) {
                        const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY
                            .replace('[id]', playlistId)
                            .replace('[songId]', sId);
                        if (window.cacheInstance) {
                            window.cacheInstance.removeItem(songKey);
                        }
                        await dbService.removeItem(songKey);
                    }
                }
            }

            // 3. Remove playlist cache key
            if (window.cacheInstance) {
                window.cacheInstance.removeItem(playlistKey);
            }
            await dbService.removeItem(playlistKey);

            // 4. Update registry
            const registry = await this.getRegistry();
            if (registry[playlistId]) {
                delete registry[playlistId];
                await this.saveRegistry(registry);
            }

            // 5. Clean from CacheStorage if available
            if (typeof caches !== 'undefined') {
                try {
                    const cache = await caches.open(CONTENT_CACHE_NAME);
                    const requests = await cache.keys();
                    for (const req of requests) {
                        const url = new URL(req.url);
                        if (url.pathname.includes(`/api/playlist/${playlistId}`)) {
                            await cache.delete(req);
                        }
                    }
                } catch (e) {
                    console.warn('[OfflinePlaylistService] CacheStorage cleanup error:', e);
                }
            }

            appEvents.emit('offline:playlist-removed', { playlistId });
            return true;
        } catch (e) {
            console.error('[OfflinePlaylistService] Error removing offline playlist:', e);
            throw e;
        }
    }

    /**
     * Estimates offline storage usage and downloaded counts
     * @returns {Promise<{ usageBytes: number, quotaBytes: number, formattedUsage: string, playlistCount: number, songCount: number }>}
     */
    async getStorageUsage() {
        const downloaded = await this.getDownloadedPlaylists();
        const playlistCount = downloaded.length;
        const songCount = downloaded.reduce((acc, p) => acc + (p.songCount || (p.songs ? p.songs.length : 0)), 0);

        let usageBytes = 0;
        let quotaBytes = 0;

        if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
            try {
                const estimate = await navigator.storage.estimate();
                usageBytes = estimate.usage || 0;
                quotaBytes = estimate.quota || 0;
            } catch (e) {
                console.warn('[OfflinePlaylistService] Error estimating storage:', e);
            }
        }

        // Fallback estimate if browser doesn't report usageBytes (e.g. ~12KB per song + metadata)
        if (usageBytes === 0 && songCount > 0) {
            usageBytes = (songCount * 14000) + (playlistCount * 5000);
        }

        const formatBytes = (bytes) => {
            if (!bytes || bytes <= 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        };

        return {
            usageBytes,
            quotaBytes,
            formattedUsage: formatBytes(usageBytes),
            playlistCount,
            songCount
        };
    }

    /**
     * Clears all offline downloaded playlists and associated song caches
     */
    async clearAllOfflineData() {
        try {
            const downloaded = await this.getDownloadedPlaylists();
            for (const item of downloaded) {
                const pId = item.id || item._id;
                if (pId) {
                    await this.removePlaylist(pId);
                }
            }

            // Clear full content cache if supported
            if (typeof caches !== 'undefined') {
                try {
                    await caches.delete(CONTENT_CACHE_NAME);
                } catch (e) {
                    console.warn('[OfflinePlaylistService] Error deleting content cache:', e);
                }
            }

            // Reset registry
            await this.saveRegistry({});
            appEvents.emit('offline:all-cleared');
            return true;
        } catch (e) {
            console.error('[OfflinePlaylistService] Error clearing all offline data:', e);
            throw e;
        }
    }

    /**
     * Generates a lightweight checksum for songs list & playlist metadata to detect changes
     */
    generateSongsChecksum(songs = [], playlistData = {}) {
        const parts = [];
        // Include playlist name/title and normalized updated_at
        if (playlistData.name || playlistData.title) parts.push(playlistData.name || playlistData.title);
        
        const rawUpdated = playlistData.updated_at || playlistData.updatedAt;
        if (rawUpdated) {
            const timeNum = new Date(rawUpdated).getTime();
            parts.push(isNaN(timeNum) ? String(rawUpdated) : String(timeNum));
        }

        (songs || []).forEach(s => {
            const sId = extractSongId(s);
            const tone = s.tone !== undefined ? s.tone : (s.chord !== undefined ? s.chord : (s.key || s.data?.chord || s.data?.tone || ''));
            const rawSongUpdated = s.updated_at || s.updatedAt || s.data?.updated_at || s.data?.updatedAt || '';
            const songTimeNum = rawSongUpdated ? new Date(rawSongUpdated).getTime() : '';
            const songUpdated = isNaN(songTimeNum) ? String(rawSongUpdated) : String(songTimeNum);
            parts.push(`${sId}:${tone}:${songUpdated}`);
        });

        return parts.join('|');
    }

    /**
     * Checks if a specific downloaded playlist has remote updates available
     * @param {string} playlistId
     * @param {Object} remotePlaylist - Optional remote playlist data if already fetched
     * @returns {Promise<{ hasUpdate: boolean, reason?: string, localEntry?: Object, remotePlaylist?: Object }>}
     */
    async checkPlaylistUpdates(playlistId, remotePlaylist = null) {
        if (!playlistId) return { hasUpdate: false };

        const registry = await this.getRegistry();
        const localEntry = registry[playlistId];
        if (!localEntry || localEntry.status !== 'downloaded') {
            return { hasUpdate: false };
        }

        let remote = remotePlaylist;
        if (!remote && typeof navigator !== 'undefined' && navigator.onLine) {
            try {
                remote = await playlistService.fetchPlaylist({ id: playlistId, force: true });
            } catch (e) {
                // If offline or network error, assume no update detectable
                return { hasUpdate: false };
            }
        }

        if (!remote) return { hasUpdate: false };

        const remoteSongs = Array.isArray(remote.songs) ? remote.songs : [];
        const remoteTotal = remote.songCount !== undefined ? remote.songCount : remoteSongs.length;

        // 1. Compare song count
        if (localEntry.songCount !== undefined && localEntry.songCount !== remoteTotal) {
            console.log(`[OfflinePlaylist] Update detected for ${playlistId}: SONG_COUNT_CHANGED`, { localCount: localEntry.songCount, remoteTotal });
            return {
                hasUpdate: true,
                reason: 'SONG_COUNT_CHANGED',
                localEntry,
                remotePlaylist: remote
            };
        }

        // 2. Compare serverUpdatedAt timestamp with a 2-second tolerance threshold for clock skew / microsecond rounding
        const remoteUpdatedAt = remote.updated_at || remote.updatedAt;
        if (remoteUpdatedAt && localEntry.serverUpdatedAt) {
            const remoteTime = new Date(remoteUpdatedAt).getTime();
            const localTime = new Date(localEntry.serverUpdatedAt).getTime();
            if (!isNaN(remoteTime) && !isNaN(localTime) && (remoteTime - localTime > 2000)) {
                console.log(`[OfflinePlaylist] Update detected for ${playlistId}: TIMESTAMP_NEWER`, { remoteTime, localTime, diffMs: remoteTime - localTime });
                return {
                    hasUpdate: true,
                    reason: 'TIMESTAMP_NEWER',
                    localEntry,
                    remotePlaylist: remote
                };
            }
        }

        // 3. Compare song IDs list & order
        if (Array.isArray(localEntry.songIds) && remoteSongs.length > 0) {
            const remoteIds = remoteSongs.map(s => extractSongId(s)).filter(Boolean);
            if (remoteIds.length !== localEntry.songIds.length || !remoteIds.every((id, idx) => id === localEntry.songIds[idx])) {
                console.log(`[OfflinePlaylist] Update detected for ${playlistId}: SONG_ORDER_OR_IDS_CHANGED`, { localIds: localEntry.songIds, remoteIds });
                return {
                    hasUpdate: true,
                    reason: 'SONG_ORDER_OR_IDS_CHANGED',
                    localEntry,
                    remotePlaylist: remote
                };
            }
        }

        // 4. Compare playlist name
        const remoteName = remote.name || remote.title;
        if (remoteName && localEntry.name && remoteName.trim() !== localEntry.name.trim()) {
            console.log(`[OfflinePlaylist] Update detected for ${playlistId}: NAME_CHANGED`, { localName: localEntry.name, remoteName });
            return {
                hasUpdate: true,
                reason: 'NAME_CHANGED',
                localEntry,
                remotePlaylist: remote
            };
        }

        // 5. If remote songs are fully hydrated with song objects/lyrics/tones, compare songsChecksum
        const isRemoteHydrated = remoteSongs.length === 0 || remoteSongs.some(s => Boolean(
            s.data || s.chord !== undefined || s.tone !== undefined || s.modifiedLyric || s.processed
        ));

        if (isRemoteHydrated && localEntry.songsChecksum) {
            let remoteChecksum = this.generateSongsChecksum(remoteSongs, remote);
            if (remoteChecksum && localEntry.songsChecksum !== remoteChecksum) {
                // If remote was passed in as a lightweight prop (e.g. from Dashboard listing),
                // fetch the authoritative fresh playlist before concluding there is an actual update
                if (remotePlaylist && typeof navigator !== 'undefined' && navigator.onLine) {
                    try {
                        const fresh = await playlistService.fetchPlaylist({ id: playlistId, force: true });
                        if (fresh) {
                            const freshSongs = Array.isArray(fresh.songs) ? fresh.songs : [];
                            const freshChecksum = this.generateSongsChecksum(freshSongs, fresh);
                            if (freshChecksum && localEntry.songsChecksum === freshChecksum) {
                                // False positive caused by dashboard props vs full playlist differences
                                return { hasUpdate: false, localEntry, remotePlaylist: fresh };
                            }
                            remoteChecksum = freshChecksum;
                            remote = fresh;
                        }
                    } catch (e) {
                        // ignore fetch error and proceed with original remoteChecksum
                    }
                }

                if (remoteChecksum && localEntry.songsChecksum !== remoteChecksum) {
                    console.log(`[OfflinePlaylist] Update detected for ${playlistId}: CHECKSUM_MISMATCH`, { local: localEntry.songsChecksum, remote: remoteChecksum });
                    return {
                        hasUpdate: true,
                        reason: 'CHECKSUM_MISMATCH',
                        localEntry,
                        remotePlaylist: remote
                    };
                }
            }
        }

        return { hasUpdate: false, localEntry, remotePlaylist: remote };
    }

    /**
     * Checks all downloaded playlists for available updates
     * @param {Array} availableRemotePlaylists - Optional list of playlists already loaded in UI/Dashboard
     * @returns {Promise<Map<string, { hasUpdate: boolean, reason?: string }>>}
     */
    async checkAllDownloadedUpdates(availableRemotePlaylists = []) {
        const downloaded = await this.getDownloadedPlaylists();
        const results = new Map();

        const remoteMap = new Map();
        (availableRemotePlaylists || []).forEach(p => {
            const id = String(p.id || p._id || '');
            if (id) remoteMap.set(id, p);
        });

        for (const item of downloaded) {
            const id = String(item.id || item._id);
            const remote = remoteMap.get(id) || null;
            const updateInfo = await this.checkPlaylistUpdates(id, remote);
            if (updateInfo.hasUpdate) {
                results.set(id, updateInfo);
            }
        }

        appEvents.emit('offline:updates-checked', { updateCount: results.size, updates: results });
        return results;
    }

    /**
     * Performs a smart differential sync on an existing downloaded playlist
     * @param {string} playlistId
     * @param {Function} onProgress
     */
    async syncPlaylist(playlistId, onProgress = null) {
        if (!playlistId) return false;

        // Fetch fresh playlist definition
        let freshPlaylist = null;
        try {
            freshPlaylist = await playlistService.fetchPlaylist({ id: playlistId, force: true });
        } catch (err) {
            console.warn('[OfflinePlaylistService] syncPlaylist: fetchPlaylist failed:', err);
            return false;
        }

        if (!freshPlaylist) return false;

        // Use downloadPlaylist which caches fresh songs, cleans up previous keys, and updates registry
        await this.downloadPlaylist(freshPlaylist, onProgress);
        appEvents.emit('offline:playlist-synced', { playlistId });
        return true;
    }

    /**
     * Synchronizes all downloaded playlists that have detected updates
     * @param {Array} targetPlaylistIds - Optional array of playlist IDs to sync, or all updated if empty
     * @param {Function} onProgress - Global progress callback ({ current, total, playlistName })
     */
    async syncAllDownloadedPlaylists(targetPlaylistIds = null, onProgress = null) {
        const downloaded = await this.getDownloadedPlaylists();
        const idsToSync = targetPlaylistIds || downloaded.map(p => p.id || p._id);
        const total = idsToSync.length;
        let syncedCount = 0;

        for (let i = 0; i < total; i++) {
            const pId = idsToSync[i];
            const pName = downloaded.find(p => (p.id || p._id) === pId)?.name || 'Playlist';

            if (onProgress) {
                onProgress({ current: i + 1, total, playlistName: pName });
            }

            try {
                await this.syncPlaylist(pId);
                syncedCount++;
            } catch (e) {
                console.warn(`[OfflinePlaylistService] Failed to auto-sync playlist ${pId}:`, e);
            }
        }

        appEvents.emit('offline:all-synced', { syncedCount, total });
        return { syncedCount, total };
    }
}

export const offlinePlaylistService = new OfflinePlaylistService();
export default offlinePlaylistService;
